import { DailyScrumComment } from '@/common/types/databaseTypes';
import { DailyScrumCommentContent } from '@/features/notification/constants/notification';
import { useDailyScrumCommentNotification } from '@/features/notification/services/factoryNotificationServices';
import { queryClient } from '@/queryClient';
import { useAuthStore } from '@/stores/authStore';
import { useMutation } from '@tanstack/react-query';
import {
    CreateDailyScrumCommentParams,
    dailyScrumCommentService,
} from './dailyScrumCommentService';
import { dailyScrumService } from './dailyScrumService';
import { DAILYSCRUM_KEYS } from './useDailyScrumQueries';

/**
 * 데일리스크럼 댓글 생성 뮤테이션
 */
export const useCreateDailyScrumComment = (teamId?: string) => {
    const { createDailyScrumCommentNotification } =
        useDailyScrumCommentNotification();

    return useMutation({
        mutationFn: (newComment: CreateDailyScrumCommentParams) => {
            return dailyScrumCommentService.createDailyScrumComment(newComment);
        },
        onSuccess: async (createdComment, newComment) => {
            void queryClient.invalidateQueries({
                queryKey: teamId
                    ? DAILYSCRUM_KEYS.byTeamId(teamId)
                    : DAILYSCRUM_KEYS.all,
            });

            await sendCommentNotification(
                createdComment,
                newComment,
                createDailyScrumCommentNotification
            );
        },
        onError: error => {
            console.error('Error creating daily scrum comment:', error);
        },
    });
};

/**
 * 데일리스크럼 댓글 알림을 발송하는 함수
 */
async function sendCommentNotification(
    createdComment: DailyScrumComment,
    newComment: CreateDailyScrumCommentParams,
    createNotification: (params: {
        userId: string;
        params: DailyScrumCommentContent;
    }) => Promise<unknown>
) {
    try {
        const { user } = useAuthStore.getState();
        if (!user) {
            console.error(
                'DailyScrum 댓글 알림 발송 실패: 로그인 정보가 없습니다.'
            );
            return;
        }

        const dailyScrumItem = await dailyScrumService.getDailyScrumItemById(
            newComment.daily_scrum_item_id
        );

        let notificationReceiverId = dailyScrumItem.user_id;

        // 답글인 경우 부모 댓글 작성자에게 알림 발송
        if (createdComment.parent_comment_id) {
            const parentRegisterId =
                await dailyScrumCommentService.getDailyScrumCommentRegisterIdById(
                    createdComment.parent_comment_id
                );

            if (parentRegisterId) {
                notificationReceiverId = parentRegisterId;
            }
        }

        // 알림 발송
        await createNotification({
            userId: notificationReceiverId,
            params: {
                comment_id: createdComment.id,
                comment_text: createdComment.content,
                is_reply: Boolean(createdComment.parent_comment_id),
                parent_comment_id: createdComment.parent_comment_id || '',
                daily_scrum_id: dailyScrumItem.daily_scrum_id,
                daily_scrum_date: dailyScrumItem.daily_scrum.date,
                daily_scrum_item_id: dailyScrumItem.id,
                commenter_name: user.name,
                commenter_image_url: user.profile_image_url || '',
                commenter_id: user.id,
                team_id: dailyScrumItem.daily_scrum.team_id,
            },
        });
    } catch (error) {
        console.error('DailyScrum 댓글 알림 발송 실패:', error);
    }
}

/**
 * 데일리스크럼 댓글 수정 뮤테이션
 */
export const useUpdateDailyScrumComment = (teamId?: string) => {
    return useMutation({
        mutationFn: ({
            commentId,
            content,
        }: {
            commentId: string;
            content: string;
        }) =>
            dailyScrumCommentService.updateDailyScrumComment(
                commentId,
                content
            ),
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: teamId
                    ? DAILYSCRUM_KEYS.byTeamId(teamId)
                    : DAILYSCRUM_KEYS.all,
            });
        },
    });
};

/**
 * 데일리스크럼 댓글 삭제 뮤테이션
 */
export const useDeleteDailyScrumComment = (teamId?: string) => {
    return useMutation({
        mutationFn: (id: string) =>
            dailyScrumCommentService.deleteDailyScrumComment(id),
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: teamId
                    ? DAILYSCRUM_KEYS.byTeamId(teamId)
                    : DAILYSCRUM_KEYS.all,
            });
        },
    });
};
